export const STUDIO_CALLBACK_BOOKING_REVIEW_MODEL_VERSION = "a31.1_v1" as const;

export type StudioCallbackBookingReviewItemTone = "clear" | "watch";

export type StudioCallbackBookingReviewItem = {
  key: "callback_backlog" | "booking_exceptions";
  label: string;
  count: number;
  tone: StudioCallbackBookingReviewItemTone;
  detail: string;
  nextAction: string;
};

export type StudioCallbackBookingReviewModel = {
  version: typeof STUDIO_CALLBACK_BOOKING_REVIEW_MODEL_VERSION;
  headline: string;
  summary: string;
  reviewOrder: StudioCallbackBookingReviewItem[];
  nextAction: string;
  escalationCue: string;
};

type StudioCallbackBookingReviewInput = {
  callbackBacklogCount: number;
  pendingCallbackCount: number;
  awaitingConfirmationCount: number;
  needsStaffCount: number;
};

function toneForCount(count: number): StudioCallbackBookingReviewItemTone {
  return count > 0 ? "watch" : "clear";
}

export function buildStudioCallbackBookingReviewModel(
  input: StudioCallbackBookingReviewInput
): StudioCallbackBookingReviewModel {
  const callbackBacklogCount = Math.max(0, input.callbackBacklogCount);
  const pendingCallbackCount = Math.max(0, input.pendingCallbackCount);
  const awaitingConfirmationCount = Math.max(0, input.awaitingConfirmationCount);
  const needsStaffCount = Math.max(0, input.needsStaffCount);
  const bookingExceptionCount = awaitingConfirmationCount + needsStaffCount;

  const reviewOrder: StudioCallbackBookingReviewItem[] = [
    {
      key: "callback_backlog",
      label: "Callback backlog",
      count: callbackBacklogCount,
      tone: toneForCount(callbackBacklogCount),
      detail:
        callbackBacklogCount > 0
          ? `${callbackBacklogCount} callback requests need review before booking drift is treated as separate follow-up work.`
          : "No callback backlog is blocking booking-exception review.",
      nextAction:
        callbackBacklogCount > 0
          ? pendingCallbackCount > 0
            ? "Acknowledge or move pending callback work first so same-day follow-up has an owner."
            : "Confirm the remaining callback queue has a clear owner and current status before moving on."
          : "Move to booking-exception review.",
    },
    {
      key: "booking_exceptions",
      label: "Booking exceptions",
      count: bookingExceptionCount,
      tone: toneForCount(bookingExceptionCount),
      detail:
        bookingExceptionCount > 0
          ? `${awaitingConfirmationCount} awaiting confirmation and ${needsStaffCount} needing staff follow-up remain operator-bounded recovery work.`
          : "No booking exceptions are waiting on confirmation or staff recovery.",
      nextAction:
        bookingExceptionCount > 0
          ? awaitingConfirmationCount > 0
            ? "Review awaiting-confirmation items next, then clear needs-staff cases that still lack a same-day owner."
            : "Clear needs-staff booking follow-up next."
          : "No booking-exception action is blocking normal queue work.",
    },
  ];

  const headline =
    callbackBacklogCount > 0
      ? "Callback backlog first"
      : bookingExceptionCount > 0
      ? "Booking exceptions next"
      : "Review path clear";

  const nextAction =
    callbackBacklogCount > 0
      ? reviewOrder[0].nextAction
      : bookingExceptionCount > 0
      ? reviewOrder[1].nextAction
      : "Callback backlog and booking exceptions are clear enough for normal queue work.";

  const escalationCue =
    callbackBacklogCount > 0 && bookingExceptionCount > 0
      ? "If callback backlog and booking exceptions both stay open across repeated review passes, escalate same day before treating the queue as stable."
      : callbackBacklogCount > 0
      ? "If callback backlog keeps aging after the first pass, escalate same day."
      : bookingExceptionCount > 0
      ? "If booking exceptions stay unowned after callback review is clear, escalate same day."
      : "No callback or booking-exception pressure currently requires escalation.";

  return {
    version: STUDIO_CALLBACK_BOOKING_REVIEW_MODEL_VERSION,
    headline,
    summary:
      "Review callback backlog before booking exceptions so follow-up drift stays visible and operator-bounded recovery work is handled in order.",
    reviewOrder,
    nextAction,
    escalationCue,
  };
}
